import { useEffect, useState } from "react";
import { Trophy, X } from "@phosphor-icons/react";
import { useWonChime, playWonChime } from "@/lib/useWonChime";

const fmt$ = (n) => {
  const v = Number(n || 0);
  if (v >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000) return `$${(v / 1_000).toFixed(0)}K`;
  return `$${Math.round(v)}`;
};

/**
 * A governed "recognition" flourish for the one moment on the pipeline that
 * qualifies as approved / celebrated: an opportunity crossing into Won.
 *
 * Contract:
 *   • Only rendered when the parent passes a fresh `event` (a `{name, value,
 *     firedAt}` bundle). Each new `firedAt` re-triggers the animation.
 *   • Auto-dismisses after 4.5 s (fade-out overlaps the last 0.6 s).
 *   • Uses Brass tokens exclusively — this is the sanctioned Brass moment
 *     the whole palette rule was written for.
 *   • Position: top of the main pane (below the app header), centred, thin
 *     ribbon. Non-blocking (pointer-events on the dismiss only).
 */
export default function WonCelebrationRibbon({ event, onDismiss }) {
  const [visible, setVisible] = useState(false);
  const [leaving, setLeaving] = useState(false);
  const { enabled: chimeEnabled } = useWonChime();

  useEffect(() => {
    if (!event) return;
    setVisible(true);
    setLeaving(false);
    // Play the chime only if the partner has explicitly opted in via Settings.
    // Off by default — the ribbon itself is the primary recognition; sound is
    // an additional flourish partners can enable one at a time.
    if (chimeEnabled) { try { playWonChime(); } catch (_) { /* audio blocked, no-op */ } }
    // Fade out just before we tell the parent to clear the event.
    const leaveTimer = setTimeout(() => setLeaving(true), 3900);
    const doneTimer = setTimeout(() => {
      setVisible(false);
      onDismiss?.();
    }, 4500);
    return () => {
      clearTimeout(leaveTimer);
      clearTimeout(doneTimer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [event?.firedAt]);

  if (!event || !visible) return null;

  return (
    <div
      className={`fixed left-1/2 z-[80] pointer-events-none ${
        leaving ? "won-ribbon-leave" : "won-ribbon-enter"
      }`}
      style={{ top: 24, transform: "translateX(-50%)" }}
      role="status"
      aria-live="polite"
      data-testid="won-celebration-ribbon"
    >
      <div
        className="flex items-center gap-3 px-5 py-2.5 border-brass bg-brass-soft backdrop-blur-md"
        style={{ borderWidth: 1, boxShadow: "0 8px 30px hsl(var(--brass) / 0.20)" }}
      >
        <Trophy size={16} weight="fill" className="text-brass shrink-0" />
        <div className="min-w-0">
          <div className="text-[0.625rem] font-mono uppercase tracking-[0.28em] text-brass">Won · recognition</div>
          <div className="text-sm font-display font-bold text-foreground truncate max-w-[420px]">
            {event.name}
            {event.value ? (
              <span className="ml-2 font-mono text-brass tabular-nums" data-testid="won-ribbon-value">
                {fmt$(event.value)}
              </span>
            ) : null}
          </div>
        </div>
        <button
          onClick={() => { setLeaving(true); setTimeout(() => { setVisible(false); onDismiss?.(); }, 400); }}
          className="pointer-events-auto text-brass hover:opacity-70 ml-1 shrink-0"
          aria-label="Dismiss recognition"
          data-testid="won-ribbon-dismiss"
        >
          <X size={12} weight="bold" />
        </button>
      </div>
    </div>
  );
}
